import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/store/useAppStore';
import { Role } from '@/types';
import { queryKeys } from '@/lib/queryKeys';
import { ProviderGamificationService } from '../services/ProviderGamificationService';
import { BADGE_DEFINITIONS } from '../constants/badges';
import type { BadgeNotificationData } from '../types/gamification.types';

export function useProviderGamification() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [notificationQueue, setNotificationQueue] = useState<BadgeNotificationData[]>([]);

  // Derived state: show one notification at a time (head of the queue)
  const currentNotification = notificationQueue.length > 0 ? notificationQueue[0] : null;

  const isPrestataire = profile?.role === Role.PRESTATAIRE;
  const userId = profile?.id;

  const { data: gamificationState, refetch } = useQuery({
    queryKey: queryKeys.gamification.detail(userId ?? ''),
    queryFn: () => ProviderGamificationService.getGamification(userId!),
    enabled: !!userId && isPrestataire,
    staleTime: 30_000,
  });

  // Adjust state when data changes (render-time comparison, official React pattern):
  // enqueue unshown badges whenever the gamification query data changes
  const [prevGamificationState, setPrevGamificationState] = useState<typeof gamificationState>(undefined);
  if (gamificationState !== prevGamificationState) {
    setPrevGamificationState(gamificationState);

    if (gamificationState && userId) {
      const unshown = ProviderGamificationService.getUnshownBadges(gamificationState);

      const newNotifications: BadgeNotificationData[] = unshown
        .map((b) => {
          const def = BADGE_DEFINITIONS[b.id];
          if (!def) return null;
          return { badge: def, unlockedAt: b.unlocked_at };
        })
        .filter(Boolean) as BadgeNotificationData[];

      if (newNotifications.length > 0) {
        setNotificationQueue((prev) => {
          // Avoid duplicates
          const existingIds = new Set(prev.map((n) => n.badge.id));
          const fresh = newNotifications.filter((n) => !existingIds.has(n.badge.id));
          return fresh.length > 0 ? [...prev, ...fresh] : prev;
        });
      }
    }
  }

  // React Compiler mémoïse automatiquement
  const unlockBadge = async (badgeId: string) => {
    if (!userId || !isPrestataire) return;
    if (!BADGE_DEFINITIONS[badgeId]) return;

    // Already unlocked?
    if (gamificationState?.badges.some((b) => b.id === badgeId)) return;

    try {
      const wasNew = await ProviderGamificationService.unlockBadge(userId, badgeId);
      if (wasNew) {
        // Refetch to get updated state
        await refetch();
      }
    } catch {
      // Silently ignore gamification errors - should not break the app
    }
  };

  const hasBadge = (badgeId: string): boolean => {
    return gamificationState?.badges.some((b) => b.id === badgeId) ?? false;
  };

  const dismissNotification = async () => {
    if (!currentNotification || !userId) return;

    try {
      await ProviderGamificationService.markBadgesAsShown(userId, [currentNotification.badge.id]);
      queryClient.invalidateQueries({ queryKey: queryKeys.gamification.all });
    } catch {
      // Ignore
    }

    // Pop the dismissed notification: the next one (if any) becomes current
    setNotificationQueue((prev) => prev.slice(1));
  };

  return {
    unlockBadge,
    hasBadge,
    currentNotification,
    dismissNotification,
    gamificationState,
    isPrestataire,
  };
}
